import { Typography, Box, Avatar, Select, MenuItem } from '@mui/material';
import { useState, useEffect } from 'react';
import useAuth from '../../../hooks/useAuth';
import gtm from '../../../lib/gtm';
import * as React from 'react';
import { getTone, toneAlpha, grey, lightVeil, darkVeil } from '../../../theme/tones';

const FinanceHeur = ({ selectedYear, handleYearChange }) => {
  const { user } = useAuth();
  const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  const [temps, setTemps] = useState(new Date().toLocaleTimeString('fr-FR'));

  useEffect(() => {
    gtm.push({ event: 'page_view' });

    const interval = setInterval(() => {
      setTemps(new Date().toLocaleTimeString('fr-FR'));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        mb: 3,
        gap: 2,
        width: '100%',
        p: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {user?.photo ? (
          <Avatar
            src={`data:image/png;base64,${user.photo}`}
            sx={{
              width: 48,
              height: 48,
              boxShadow: `0 4px 12px 0 ${darkVeil(0.08)}`,
              border: `2px solid ${grey(0)}`,
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontWeight: 700,
              fontSize: '1.1rem',
              boxShadow: `0 4px 12px 0 ${darkVeil(0.08)}`,
            }}
          >
            {user?.firstname ? user.firstname.charAt(0).toUpperCase() : 'A'}
          </Avatar>
        )}
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: 'text.primary',
              letterSpacing: '-0.5px',
              mb: 0.2,
              fontSize: { xs: '1.4rem', md: '1.8rem' }
            }}
          >
            Tableau de bord
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.9rem'
            }}
          >
            Ravi de vous revoir,{' '}
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
              {user?.firstname || 'Administrateur'}
            </Box>{' '}
            👋
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: (theme) => (theme.palette.mode === 'light' ? lightVeil(0.85) : toneAlpha('neutral', 0.45)),
          backdropFilter: 'blur(12px)',
          borderRadius: '24px',
          padding: '8px 20px',
          boxShadow: (theme) => (theme.palette.mode === 'light' 
            ? `0 4px 16px -2px ${darkVeil(0.04)}, inset 0 0 0 1px ${lightVeil(0.6)}`
            : `0 4px 16px -2px ${darkVeil(0.25)}, inset 0 0 0 1px ${lightVeil(0.05)}`),
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Date Segment */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              fontSize: '0.62rem',
              mb: 0.1,
            }}
          >
            Date
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              color: 'text.primary',
              fontWeight: 700,
              textTransform: 'capitalize',
              fontSize: '0.82rem',
            }}
          >
            {new Date().toLocaleDateString('fr-FR', options)}
          </Typography>
        </Box>

        <Box sx={{ width: '1px', height: '24px', backgroundColor: 'divider' }} />

        {/* Time Segment */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              fontSize: '0.62rem',
              mb: 0.1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            Heure live
            <Box
              component="span"
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: getTone('emerald').base,
                display: 'inline-block',
                boxShadow: `0 0 0 0 ${toneAlpha('emerald', 0.7)}`,
                animation: 'pulse 1.8s infinite cubic-bezier(0.66, 0, 0, 1)',
                '@keyframes pulse': {
                  '0%': {
                    transform: 'scale(0.95)',
                    boxShadow: `0 0 0 0 ${toneAlpha('emerald', 0.7)}`,
                  },
                  '70%': {
                    transform: 'scale(1)',
                    boxShadow: `0 0 0 5px ${toneAlpha('emerald', 0)}`,
                  },
                  '100%': {
                    transform: 'scale(0.95)',
                    boxShadow: `0 0 0 0 ${toneAlpha('emerald', 0)}`,
                  },
                },
              }}
            />
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              color: 'text.primary',
              fontWeight: 700,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
            }}
          >
            {temps}
          </Typography>
        </Box>

        {/* Year Segment (Embedded Select if props are provided) */}
        {selectedYear !== undefined && handleYearChange && (
          <>
            <Box sx={{ width: '1px', height: '24px', backgroundColor: 'divider' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  fontSize: '0.62rem',
                  mb: 0.1,
                }}
              >
                Année
              </Typography>
              <Select
                value={selectedYear}
                onChange={handleYearChange}
                variant="standard"
                disableUnderline
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  fontSize: '0.85rem',
                  '& .MuiSelect-select': {
                    py: 0,
                    pr: '18px !important',
                    pl: 0,
                  },
                  '& .MuiSelect-icon': {
                    color: 'primary.main',
                    right: 0,
                    top: 'calc(50% - 10px)',
                  }
                }}
              >
                {[2022, 2023, 2024, 2025, 2026].map((y) => (
                  <MenuItem key={y} value={y} sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default FinanceHeur;
